import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../sidebar";
import PageHeader from "../../page_header";
import { useModalContext } from "../../CustomModal/ModalProvider.jsx";
import useAreaDelete from "../AreaDelete/AreaDelete.jsx";
import { goToGuardianNewAlert } from "../../../utils/appNavigation";
import {
  canCreateGuardianAlert,
  isPropOrGuardianUser,
} from "../../../utils/userRoles";

import {
  MainContainer,
  AreaSubtitle,
  ContentWrapper,
  SideContainer,
  StyledButtonForm,
  FirmsSection,
  FirmsSectionTitle,
  FirmsCardList,
  FirmsCard,
  FirmsCardHeader,
  FirmsCardName,
  FirmsCardBbox,
  FirmsCardStatus,
  FirmsCardActions,
  FirmsCardMeta,
  FirmsCardLink,
  FirmsEmpty,
  ActionsBar,
  InfoSection,
  InfoSectionTitle,
  InfoGrid,
  InfoFieldsColumn,
  InfoField,
  InfoLabel,
  InfoValue,
  AdiImage,
  AdiImageEmpty,
  TeamList,
  TeamChip,
  StatusBadge,
  LoadingText,
  TabPanel,
} from "./styles";

import { projectService } from "../../../services/projectService.js";
import { userService } from "../../../services/userService.js";
import { guardianAreaService } from "../../../services/guardianAreaService.js";
import { firesService } from "../../../services/firesService.js";
import VegetationSuppressionPanel from "../VegetationSuppression/Panel";
import VegetationSuppressionUnderConstruction from "../VegetationSuppression/UnderConstruction";
import AreaCamerasPanel from "../AreaCameras/Panel";
import AreaAlertsSection from "./AreaAlertsSection";

const AREA_TABS = [
  { id: "info", label: "Dados gerais" },
  { id: "firms", label: "FIRMS" },
  { id: "asv", label: "Monitoramento" },
  { id: "cameras", label: "Câmeras" },
];

const AreaDetails = () => {
  const { id_code } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.user.currentUser);
  const collapsed = useSelector((state) => state.sidebar);
  const showCreateAlert = canCreateGuardianAlert(currentUser);
  const { showConfirm, showSuccess, showError } = useModalContext();
  const { handleDelete } = useAreaDelete();

  const tabFromQuery = new URLSearchParams(location.search).get("aba");
  const initialTab = AREA_TABS.some((t) => t.id === tabFromQuery)
    ? tabFromQuery
    : AREA_TABS.some((t) => t.id === location.state?.activeTab)
      ? location.state.activeTab
      : "info";
  const [activeTab, setActiveTab] = useState(initialTab);

  const [area, setArea] = useState(null);
  const [options, setOptions] = useState({ projects: [], users: [] });
  const [formData, setFormData] = useState({
    name: "",
    project: "",
    vert_responsible: "",
    adi_responsible: "",
    other_responsibles: [],
    adi_image: null,
  });
  const [fireAreas, setFireAreas] = useState([]);
  const [fireScans, setFireScans] = useState({});
  const [fireLoading, setFireLoading] = useState(false);
  const [fireScanning, setFireScanning] = useState(null);

  useEffect(() => {
    const queryTab = new URLSearchParams(location.search).get("aba");
    if (AREA_TABS.some((t) => t.id === queryTab)) {
      setActiveTab(queryTab);
      return;
    }
    if (AREA_TABS.some((t) => t.id === location.state?.activeTab)) {
      setActiveTab(location.state.activeTab);
    }
  }, [location.state, location.search]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [areaRes, projectsRes, usersRes] = await Promise.all([
          guardianAreaService.getGuardianAreaById(id_code),
          projectService.getProjects(),
          userService.getUsers(),
        ]);

        setArea(areaRes.data || areaRes);

        const normalizeArray = (res) => {
          if (Array.isArray(res)) return res;
          if (Array.isArray(res?.data)) return res.data;
          if (Array.isArray(res?.data?.data)) return res.data.data;
          if (Array.isArray(res?.projects)) return res.projects;
          if (Array.isArray(res?.users)) return res.users;
          return [];
        };

        setOptions({
          projects: normalizeArray(projectsRes),
          users: normalizeArray(usersRes),
        });

        const raw = areaRes.data || areaRes;
        const resolvePerson = (value, nameFallback) => {
          if (nameFallback) return nameFallback;
          if (value && typeof value === "object") {
            return value.full_name || value.name || "";
          }
          return value ?? "";
        };

        setFormData({
          name: raw?.name || "",
          project: String(
            raw?.project_detail?.title ||
              raw?.project_name ||
              raw?.project ||
              ""
          ),
          vert_responsible: resolvePerson(
            raw?.vert_responsible,
            raw?.vert_responsible_name
          ),
          adi_responsible: resolvePerson(
            raw?.adi_responsible,
            raw?.adi_responsible_name
          ),
          other_responsibles: (raw?.other_responsibles || []).map(
            (user, index) => {
              if (user && typeof user === "object") return user;
              const name = raw?.other_responsibles_names?.[index];
              return name ? { id: user, full_name: name } : user;
            }
          ),
          adi_image: raw?.adi_image || null,
        });
      } catch (error) {
        console.error("Erro ao carregar dados:", error);
      }
    };

    fetchData();
  }, [id_code]);

  const guardianAreaId = area?.id_code || area?.id;

  useEffect(() => {
    if (!guardianAreaId || activeTab !== "firms") return;
    const fetchFireAreas = async () => {
      setFireLoading(true);
      try {
        const data = await firesService.listAreas(guardianAreaId);
        setFireAreas(Array.isArray(data) ? data : []);
      } catch {
        setFireAreas([]);
      } finally {
        setFireLoading(false);
      }
    };
    fetchFireAreas();
  }, [guardianAreaId, activeTab]);

  useEffect(() => {
    if (fireAreas.length === 0) return;
    const fetchScans = async () => {
      const scansMap = {};
      await Promise.all(
        fireAreas.map(async (fa) => {
          try {
            const list = await firesService.listAreaScans(fa.id, 1);
            if (list?.[0]) scansMap[fa.id] = list[0];
          } catch {
            /* ignore */
          }
        })
      );
      setFireScans(scansMap);
    };
    fetchScans();
  }, [fireAreas]);

  const getUserName = useMemo(() => {
    const byId = new Map(
      (options.users || []).map((u) => [String(u.id), u.full_name || u.name])
    );
    return (idOrObjOrName) => {
      if (idOrObjOrName == null || idOrObjOrName === "") return "—";
      if (typeof idOrObjOrName === "object") {
        return idOrObjOrName.full_name || idOrObjOrName.name || "—";
      }
      const asString = String(idOrObjOrName);
      if (byId.has(asString)) return byId.get(asString);
      if (/^\d+$/.test(asString)) return "—";
      return asString;
    };
  }, [options.users]);

  const handleRunFireScan = async (fireAreaId) => {
    setFireScanning(fireAreaId);
    try {
      const result = await firesService.runAreaScan(fireAreaId, 1);
      setFireScans((prev) => ({ ...prev, [fireAreaId]: result }));
    } catch (err) {
      console.error("Erro ao executar scan FIRMS:", err);
    } finally {
      setFireScanning(null);
    }
  };

  const archiveArea = async (e) => {
    e.preventDefault();

    const confirmed = await showConfirm(
      "Arquivar Área?",
      "Deseja realmente arquivar esta área?"
    );

    if (!confirmed) return;

    try {
      await guardianAreaService.archiveGuardianArea(area.id_code);
      showSuccess("Sucesso!", "Área arquivada com sucesso.");
      navigate("/areas");
    } catch (error) {
      console.error("❌ [DEBUG] Erro ao arquivar área:", error);
      showError(
        "Erro!",
        error.response?.data?.message || "Erro ao arquivar a área."
      );
    }
  };

  const openTab = (tabId) => {
    setActiveTab(tabId);
    const params = new URLSearchParams(location.search);
    if (tabId === "info") params.delete("aba");
    else params.set("aba", tabId);
    const search = params.toString();
    navigate(
      { pathname: ".", search: search ? `?${search}` : "" },
      { replace: true, state: { ...(location.state || {}), activeTab: tabId } }
    );
  };

  const adiImageSrc = useMemo(() => {
    if (!formData.adi_image) return null;
    return typeof formData.adi_image === "string"
      ? formData.adi_image
      : URL.createObjectURL(formData.adi_image);
  }, [formData.adi_image]);

  if (!area) {
    return (
      <MainContainer>
        <Sidebar />
        <SideContainer collapsed={collapsed}>
          <LoadingText>Carregando dados da área...</LoadingText>
        </SideContainer>
      </MainContainer>
    );
  }

  const pageTitle = formData.name || "Informações da Área";

  return (
    <MainContainer>
      <Sidebar />
      <SideContainer collapsed={collapsed}>
        <ContentWrapper>
          <PageHeader
            title={pageTitle}
            tabs={AREA_TABS}
            activeTab={activeTab}
            onTabChange={openTab}
          />

          {(formData.project || area.archived) && (
            <AreaSubtitle style={{ marginTop: "-0.35rem", marginBottom: "0.85rem" }}>
              {formData.project ? formData.project : ""}
              {formData.project && area.archived ? " · " : ""}
              {area.archived ? "Arquivada" : ""}
            </AreaSubtitle>
          )}

          <TabPanel>
            {activeTab === "info" && (
              <>
                <ActionsBar>
                  {!area.archived ? (
                    <>
                      {showCreateAlert && (
                        <StyledButtonForm
                          type="button"
                          onClick={() => {
                            const rawProject =
                              area.project_detail?.id ??
                              (typeof area.project === "object"
                                ? area.project?.id
                                : area.project);
                            goToGuardianNewAlert(dispatch, navigate, {
                              prefillAlert: {
                                project:
                                  rawProject != null && rawProject !== ""
                                    ? String(rawProject)
                                    : "",
                                guardian_area: String(area.id_code),
                                area_name: area.name || "",
                              },
                            });
                          }}
                        >
                          Criar alerta
                        </StyledButtonForm>
                      )}
                      <StyledButtonForm
                        type="button"
                        onClick={() => navigate(`/edit_area/${area.id_code}`)}
                      >
                        Editar Área
                      </StyledButtonForm>
                      <StyledButtonForm type="button" onClick={archiveArea}>
                        Arquivar Área
                      </StyledButtonForm>
                      <StyledButtonForm
                        type="button"
                        onClick={() => navigate(`/alerts/history/${id_code}`)}
                      >
                        Histórico
                      </StyledButtonForm>
                      <StyledButtonForm
                        type="button"
                        onClick={() => handleDelete(area.id_code, area.name)}
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--danger) 22%, transparent)",
                          borderColor: "var(--danger)",
                          color: "var(--danger)",
                        }}
                      >
                        Excluir Área
                      </StyledButtonForm>
                    </>
                  ) : (
                    <>
                      <StyledButtonForm
                        type="button"
                        onClick={() =>
                          navigate(`/alerts/history/${area.id_code}`)
                        }
                      >
                        Histórico
                      </StyledButtonForm>
                      <StyledButtonForm
                        type="button"
                        onClick={() => handleDelete(area.id_code, area.name)}
                        style={{
                          backgroundColor:
                            "color-mix(in srgb, var(--danger) 22%, transparent)",
                          borderColor: "var(--danger)",
                          color: "var(--danger)",
                        }}
                      >
                        Excluir Área
                      </StyledButtonForm>
                    </>
                  )}
                </ActionsBar>

                <InfoSection>
                  <InfoSectionTitle>Dados gerais</InfoSectionTitle>
                  <InfoGrid>
                    <InfoField>
                      <InfoLabel>Imagem ADI</InfoLabel>
                      {adiImageSrc ? (
                        <AdiImage
                          src={adiImageSrc}
                          alt="ADI"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                      ) : (
                        <AdiImageEmpty>Sem imagem disponível</AdiImageEmpty>
                      )}
                    </InfoField>

                    <InfoFieldsColumn>
                      <InfoField>
                        <InfoLabel>Nome da área</InfoLabel>
                        <InfoValue>{formData.name || "—"}</InfoValue>
                      </InfoField>

                      <InfoField>
                        <InfoLabel>Projeto</InfoLabel>
                        <InfoValue>{formData.project || "—"}</InfoValue>
                      </InfoField>

                      <InfoField>
                        <InfoLabel>Responsável VERT</InfoLabel>
                        <InfoValue>
                          {getUserName(formData.vert_responsible)}
                        </InfoValue>
                      </InfoField>

                      <InfoField>
                        <InfoLabel>Gestor</InfoLabel>
                        <InfoValue>
                          {getUserName(formData.adi_responsible)}
                        </InfoValue>
                      </InfoField>

                      <InfoField data-full="true">
                        <InfoLabel>Equipe responsável (outros)</InfoLabel>
                        {formData.other_responsibles?.length > 0 ? (
                          <TeamList>
                            {formData.other_responsibles.map((user) => {
                              const key =
                                typeof user === "object" ? user.id : user;
                              return (
                                <TeamChip key={key || getUserName(user)}>
                                  {getUserName(user)}
                                </TeamChip>
                              );
                            })}
                          </TeamList>
                        ) : (
                          <InfoValue>—</InfoValue>
                        )}
                      </InfoField>
                    </InfoFieldsColumn>
                  </InfoGrid>
                </InfoSection>

                <AreaAlertsSection areaId={guardianAreaId || id_code} />
              </>
            )}

            {activeTab === "firms" && (
              <FirmsSection style={{ marginTop: 0 }}>
                <FirmsSectionTitle>
                  Áreas FIRMS (monitoramento por satélite)
                </FirmsSectionTitle>
                {!area.archived && (
                  <ActionsBar style={{ marginBottom: "0.85rem" }}>
                    <StyledButtonForm
                      type="button"
                      onClick={() =>
                        navigate(`/area_details/${area.id_code}/firms/create`)
                      }
                    >
                      + Adicionar área FIRMS
                    </StyledButtonForm>
                  </ActionsBar>
                )}
                {fireLoading ? (
                  <FirmsEmpty>Carregando áreas FIRMS...</FirmsEmpty>
                ) : fireAreas.length === 0 ? (
                  <FirmsEmpty>
                    Nenhuma área FIRMS cadastrada para esta área. Clique em
                    &quot;Adicionar área FIRMS&quot; para criar.
                  </FirmsEmpty>
                ) : (
                  <FirmsCardList>
                    {fireAreas.map((fa) => {
                      const lastScan = fireScans[fa.id];
                      const kmlUrl = fa.kml_file_url || fa.kml_file || null;
                      return (
                        <FirmsCard key={fa.id}>
                          <FirmsCardHeader>
                            <FirmsCardName>{fa.name}</FirmsCardName>
                            {lastScan && (
                              <StatusBadge $danger={Boolean(lastScan.has_risk)}>
                                {lastScan.has_risk ? "Risco" : "OK"}
                              </StatusBadge>
                            )}
                          </FirmsCardHeader>
                          <FirmsCardMeta>
                            Status: {fa.active === false ? "Inativa" : "Ativa"}
                            {kmlUrl ? " · KML disponível" : " · Sem KML"}
                          </FirmsCardMeta>
                          {fa.nasa_firms_alert_subscription_id != null && (
                            <FirmsCardMeta>
                              Assinatura NASA FIRMS:{" "}
                              {fa.nasa_firms_alert_subscription_id}
                            </FirmsCardMeta>
                          )}
                          {fa.bbox_str && (
                            <FirmsCardBbox>Bbox: {fa.bbox_str}</FirmsCardBbox>
                          )}
                          <FirmsCardStatus>
                            {lastScan
                              ? `${lastScan.detection_count} detecção(ões) — Último scan: ${new Date(
                                  lastScan.scan_at
                                ).toLocaleString("pt-BR")}`
                              : "Nenhum scan executado"}
                          </FirmsCardStatus>
                          <FirmsCardActions>
                            <StyledButtonForm
                              type="button"
                              secondary
                              onClick={() =>
                                navigate(`/firms/areas/${fa.id}`, {
                                  state: { fromAreaDetails: area.id_code },
                                })
                              }
                            >
                              Ver detalhes
                            </StyledButtonForm>
                            {kmlUrl && (
                              <FirmsCardLink
                                href={kmlUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Baixar KML
                              </FirmsCardLink>
                            )}
                            {!area.archived && (
                              <StyledButtonForm
                                type="button"
                                onClick={() => handleRunFireScan(fa.id)}
                                disabled={fireScanning === fa.id}
                              >
                                {fireScanning === fa.id
                                  ? "Executando..."
                                  : "Executar scan"}
                              </StyledButtonForm>
                            )}
                            <StyledButtonForm
                              type="button"
                              secondary
                              onClick={() =>
                                navigate(
                                  `/area_details/${area.id_code}/firms/${fa.id}/history`
                                )
                              }
                            >
                              Ver histórico
                            </StyledButtonForm>
                          </FirmsCardActions>
                        </FirmsCard>
                      );
                    })}
                  </FirmsCardList>
                )}
              </FirmsSection>
            )}

            {activeTab === "asv" &&
              (isPropOrGuardianUser(currentUser) ? (
                <VegetationSuppressionUnderConstruction />
              ) : (
                <VegetationSuppressionPanel
                  areaId={guardianAreaId}
                  archived={Boolean(area.archived)}
                />
              ))}

            {activeTab === "cameras" && (
              <AreaCamerasPanel
                areaId={guardianAreaId}
                archived={Boolean(area.archived)}
              />
            )}
          </TabPanel>
        </ContentWrapper>
      </SideContainer>
    </MainContainer>
  );
};

export default AreaDetails;
